import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { N50, N500 } from "@atlaskit/theme/colors";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { AtlassianGuardLogoBrand } from "@/libraries/wpl/foundations/src/logos/atlassian-guard-logo-brand.tsx";

const atlassianAccessContainerCss = css({
    maxWidth: 968,
    margin: "0 auto",
    paddingBlockStart: 48,
    paddingBlockEnd: 40,
    paddingInlineEnd: 48,
    display: "flex",
    [media.below.sm]: {
        flexDirection: "column",
        alignItems: "center",
        gap: 24,
        marginInlineStart: 12,
        marginInlineEnd: 12,
        paddingBlockStart: 24,
        paddingBlockEnd: 24,
        paddingInlineStart: 16,
        paddingInlineEnd: 16
    },
    boxShadow: `0px 3px 5px 0px ${N50}`,
    marginBlockEnd: 48
});

const sectionLeftCss = css({
    paddingInlineStart: 64,
    paddingInlineEnd: 64,
    display: "flex",
    alignItems: "center"
});

const sectionRightCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 16
});

const atlassianGuardLogoContainerCss = css({
    "height": 32,
    "& svg": {
        height: 32
    }
});

const headingTextCss = css({
    fontSize: 24,
    fontWeight: 700,
    lineHeight: "30px",
    margin: 0
});

const bodyTextCss = css({
    margin: 0,
    color: N500
});

export const AtlassianAccess = () => {
    const { formatMessage } = useIntl();

    return (
        <div css={atlassianAccessContainerCss}>
            <div css={sectionLeftCss}>
                <AtlassianAccessShieldIcon />
            </div>
            <div css={sectionRightCss}>
                <span css={atlassianGuardLogoContainerCss}>
                    <AtlassianGuardLogoBrand />
                </span>
                <h4 css={headingTextCss}>
                    <FormattedMessage
                        id="PricingPagesTeam24.atlassianGuard.heading"
                        defaultMessage="Safeguard your mission-critical data"
                    />
                </h4>

                <p css={bodyTextCss}>
                    <FormattedMessage
                        id="PricingPagesTeam24.atlassianGuard.body"
                        defaultMessage="Add Atlassian Guard for enhanced security and visibility across your Atlassian cloud products. Set proactive security policies, quickly detect suspicious behavior, and take action with suggested remediation steps to protect your most valuable work. Additionally, with a Cloud Enterprise plan, you get support for multiple identity providers (IdPs) and Atlassian Guard Standard."
                    />
                </p>

                <Link
                    text={formatMessage({
                        id: "PricingPages.atlassianAccess.cta",
                        defaultMessage: "Learn more"
                    })}
                    doesArrowAnimate={true}
                    type="basic-underline"
                    href={useLocalizedHref("/software/access")}
                />
            </div>
        </div>
    );
};

const AtlassianAccessShieldIcon = () => {
    return (
        <svg
            width="91"
            height="109"
            viewBox="0 0 91 109"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M51.6451 6.26856L45.4292 0.019043L39.2133 6.26856C34.1866 11.3346 27.9272 15.0041 21.0513 16.9159C14.1755 18.8277 6.92026 18.9158 0 17.1716V65.5717C0.000787638 69.8092 1.07526 73.9774 3.1231 77.6872C5.17094 81.3971 8.12539 84.5275 11.7106 86.7864L45.4292 108.03L79.1478 86.7864C82.733 84.5275 85.6875 81.3971 87.7353 77.6872C89.7831 73.9774 90.8576 69.8092 90.8584 65.5717V17.1716C83.9381 18.9158 76.6829 18.8277 69.8071 16.9159C62.9312 15.0041 56.6718 11.3346 51.6451 6.26856Z"
                fill="url(#paint0_linear_1041_5036)"
            />
            <path
                d="M45.4287 0L51.6446 6.24952C56.6693 11.3189 62.9279 14.992 69.8038 16.9072C76.6798 18.8224 83.936 18.9136 90.8579 17.1718V65.5719C90.8571 69.8094 89.7827 73.9776 87.7348 77.6874C85.687 81.3972 82.7325 84.5277 79.1473 86.7866L45.4287 108.03V0Z"
                fill="url(#paint1_linear_1041_5036)"
            />
            <g opacity="0.2">
                <path
                    opacity="0.2"
                    d="M33.0314 72.5902L16.1145 55.6973C15.9271 55.5102 15.7785 55.288 15.6771 55.0435C15.5756 54.7989 15.5234 54.5367 15.5234 54.2719C15.5234 54.0072 15.5756 53.745 15.6771 53.5004C15.7785 53.2558 15.9271 53.0337 16.1145 52.8466L21.9458 47.0153C22.1328 46.8279 22.355 46.6793 22.5996 46.5779C22.8442 46.4765 23.1063 46.4243 23.3711 46.4243C23.6359 46.4243 23.8981 46.4765 24.1426 46.5779C24.3872 46.6793 24.6094 46.8279 24.7965 47.0153L34.4544 56.6588L55.6643 35.4489C55.8518 35.2614 56.0744 35.1126 56.3194 35.0111C56.5643 34.9097 56.8269 34.8574 57.0921 34.8574C57.3572 34.8574 57.6198 34.9097 57.8648 35.0111C58.1098 35.1126 58.3323 35.2614 58.5198 35.4489L64.3511 41.2753C64.7272 41.6544 64.9382 42.1667 64.9382 42.7007C64.9382 43.2347 64.7272 43.747 64.3511 44.1261L35.8822 72.595C35.6948 72.7821 35.4723 72.9304 35.2276 73.0314C34.9828 73.1324 34.7206 73.1841 34.4558 73.1837C34.191 73.1832 33.9289 73.1306 33.6845 73.0288C33.4401 72.9269 33.2182 72.7779 33.0314 72.5902Z"
                    fill="#091E42"
                />
            </g>
            <path
                d="M38.1027 72.5906L21.1858 55.6976C20.9984 55.5105 20.8498 55.2884 20.7483 55.0438C20.6469 54.7992 20.5947 54.537 20.5947 54.2723C20.5947 54.0075 20.6469 53.7453 20.7483 53.5007C20.8498 53.2562 20.9984 53.034 21.1858 52.8469L27.017 46.9916C27.2045 46.8041 27.4271 46.6553 27.6721 46.5538C27.9171 46.4524 28.1796 46.4001 28.4448 46.4001C28.71 46.4001 28.9725 46.4524 29.2175 46.5538C29.4625 46.6553 29.6851 46.8041 29.8726 46.9916L39.5305 56.6591L60.7404 35.4492C60.9275 35.2618 61.1497 35.1132 61.3942 35.0118C61.6388 34.9104 61.901 34.8582 62.1658 34.8582C62.4305 34.8582 62.6927 34.9104 62.9373 35.0118C63.1819 35.1132 63.4041 35.2618 63.5911 35.4492L69.4224 41.2757C69.6098 41.4627 69.7584 41.6849 69.8598 41.9295C69.9613 42.1741 70.0135 42.4363 70.0135 42.701C70.0135 42.9658 69.9613 43.228 69.8598 43.4726C69.7584 43.7171 69.6098 43.9393 69.4224 44.1264L40.9535 72.5954C40.7661 72.7824 40.5436 72.9307 40.2989 73.0317C40.0541 73.1327 39.7919 73.1844 39.5271 73.184C39.2623 73.1836 39.0002 73.1309 38.7558 73.0291C38.5114 72.9273 38.2895 72.7782 38.1027 72.5906Z"
                fill="white"
            />
            <g opacity="0.6">
                <path
                    opacity="0.6"
                    d="M45.4287 98.2234L16.1329 79.768C13.7276 78.2632 11.7457 76.1698 10.3746 73.6859C9.00349 71.2019 8.28841 68.4093 8.29695 65.572V26.6856H8.77768C15.5004 26.8441 22.1852 25.6337 28.4256 23.1279C34.6659 20.6222 40.3315 16.8735 45.0778 12.1098L45.4191 11.7637L45.7604 12.1098C50.5081 16.8717 56.1739 20.6192 62.4139 23.1248C68.6539 25.6304 75.338 26.8418 82.0605 26.6856H82.5412V65.572C82.5498 68.4093 81.8347 71.2019 80.4636 73.6859C79.0925 76.1698 77.1106 78.2632 74.7053 79.768L45.4287 98.2234ZM9.25841 27.6711V65.572C9.2502 68.2459 9.92396 70.8777 11.216 73.2187C12.5081 75.5597 14.3757 77.5325 16.6425 78.9508L45.4287 97.0888L74.215 78.9508C76.4817 77.5325 78.3493 75.5597 79.6414 73.2187C80.9334 70.8777 81.6072 68.2459 81.599 65.572V27.6711C74.9089 27.7613 68.2674 26.5238 62.0587 24.0301C55.8501 21.5364 50.1977 17.8361 45.4287 13.1434C40.6609 17.8379 35.0088 21.5393 28.7999 24.0331C22.591 26.5269 15.9488 27.7634 9.25841 27.6711Z"
                    fill="#0065FF"
                />
            </g>
            <defs>
                <linearGradient
                    id="paint0_linear_1041_5036"
                    x1="45.4292"
                    y1="-0.788587"
                    x2="45.4292"
                    y2="111.895"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0.4" stopColor="#2684FF" />
                    <stop offset="0.59" stopColor="#207FFF" />
                    <stop offset="0.85" stopColor="#0E70FF" />
                    <stop offset="1" stopColor="#0065FF" />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_1041_5036"
                    x1="68.1433"
                    y1="-4.90347"
                    x2="68.1433"
                    y2="115.491"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#B2D4FF" />
                    <stop offset="0.14" stopColor="#81B8FF" />
                    <stop offset="0.27" stopColor="#59A1FF" />
                    <stop offset="0.39" stopColor="#3D91FF" />
                    <stop offset="0.5" stopColor="#2C87FF" />
                    <stop offset="0.57" stopColor="#2684FF" />
                </linearGradient>
            </defs>
        </svg>
    );
};
